import { vec3 } from 'gl-matrix';
import { Subject } from 'rxjs';

import { TimedMovement } from './TimedMovement';
import { BoundingDimensions } from '../util';
import { NavigationSystem, PathNode, PathSegment, VolumeGridLike } from '../navigation-mesh';
import { PhysicsBody } from '../physics';
import { NavigationBehaviour } from './behaviours/NavigationBehaviour';

export type { NavigationBehaviour };

export class NavigationAwareObject {
  private behaviours: Set<NavigationBehaviour>;
  public movement: TimedMovement<MovementNode>;
  public pathChanged: Subject<PathSegment[]>;
  public physicsBody?: PhysicsBody;

  constructor(
    public navigationObject: NavigationObject,
    public navigationData: NavigationData,
    speed: number,
  ) {
    this.behaviours = new Set<NavigationBehaviour>();
    const initialNode = this.getPathNode(this.navigationObject.position, 10);
    if (!initialNode) {
      console.warn('No nav node found at spawn position', this.navigationObject.position);
    }
    this.movement = new TimedMovement<MovementNode>(
      this.navigationObject,
      {
        position: this.navigationObject.position,
        node: initialNode as PathNode,
      },
      speed,
    );

    this.pathChanged = new Subject<PathSegment[]>();

    this.movement.onStep.subscribe((mv) => {
      let pos = this.navigationObject.position;
      const maxStep = this.navigationData.navigation.maxNeighbourStepHeight;
      let queryY = (pos[1] as number) + (isFinite(maxStep) ? maxStep : 1);
      let surfaceHeight = this.navigationData.volumeGrid?.getFloorHeightBelow(
        pos[0] as number,
        queryY,
        pos[2] as number,
      );
      if (surfaceHeight !== null && surfaceHeight !== undefined) {
        if (this.physicsBody) {
          this.physicsBody.surfaceHeight = surfaceHeight;
          this.physicsBody.minFloorHeight = surfaceHeight;
        } else {
          this.navigationObject.position[1] = surfaceHeight;
        }
      }
    });
  }

  public getPathNode(position: vec3 | number[], heightRange: number): PathNode | undefined {
    // Find the closest nav node containing this XZ position within Y range
    let candidates = this.navigationData.navigation.database.getObjectsInRange(
      vec3.fromValues(position[0] as number, position[1] as number, position[2] as number),
      heightRange,
    );

    let best: PathNode | undefined;
    let bestYDist = Infinity;

    for (let node of candidates) {
      let bb = node.getBoundingBox();
      // Check XZ containment
      if (
        position[0] >= bb.min[0] &&
        position[0] <= bb.max[0] &&
        position[2] >= bb.min[2] &&
        position[2] <= bb.max[2]
      ) {
        let nodeY = (((bb.min[1] as number) + bb.max[1]) as number) / 2;
        let yDist = Math.abs(nodeY - (position[1] as number));
        if (yDist < bestYDist) {
          bestYDist = yDist;
          best = node;
        }
      }
    }

    return best;
  }

  public bind(behaviour: NavigationBehaviour) {
    behaviour.attach(this);
    this.behaviours.add(behaviour);
  }

  public unbind(behaviour: NavigationBehaviour) {
    behaviour.detach(this);
    this.behaviours.delete(behaviour);
  }

  public navigateToPosition(position: vec3 | number[], options: NavigateOptions = {}) {
    let node = this.getPathNode(position, 25);
    if (!node) {
      // Nothing to navigate to
      return;
    }

    let route = this.navigationData.navigation.pathFinder.findPath(
      this.movement.currentNode!.node,
      node,
      this.navigationObject.position,
      position,
      options.maxRadius != null ? { maxRadius: options.maxRadius } : {},
    );

    if (route.solved) {
      let path = this.navigationData.navigation.pathFinder.optimizeMovementPath(
        this.movement.currentNode!.node,
        route,
      );
      this.pathChanged.next(path);
      this.movement.setPath(path);
    }
  }

  public navigateToNode(
    targetNode: PathNode,
    position: vec3 | number[],
    options: NavigateOptions = {},
  ) {
    let route = this.navigationData.navigation.pathFinder.findPath(
      this.movement.currentNode!.node,
      targetNode,
      this.navigationObject.position,
      position,
      options.maxRadius != null ? { maxRadius: options.maxRadius } : {},
    );

    if (route.solved) {
      let path = this.navigationData.navigation.pathFinder.optimizeMovementPath(
        this.movement.currentNode!.node,
        route,
      );
      this.pathChanged.next(path);
      this.movement.setPath(path);
    } else {
      console.warn('no path', this.movement.currentNode!.node, targetNode);
    }
  }
}

export type NavigateOptions = {
  /** Override the path finder's default search radius for this call. */
  maxRadius?: number;
};

type NavigationObject = {
  position: vec3 | number[];
  bounds?: BoundingDimensions;
};

type MovementNode = {
  position: vec3 | number[];
  node: PathNode;
};

type NavigationData = {
  navigation: NavigationSystem;
  volumeGrid: VolumeGridLike | null;
};
